import { z } from 'zod';

/**
 * Contratti condivisi dell'integrazione A-Cube (API ufficiali gov-it,
 * e-receipts IT e common). Unica fonte di verità per backend e frontend:
 * configurazione account (integration_templates), identità fiscale per
 * ragione sociale (companies.fiscal_provider_config) e DTO delle risposte
 * remote effettivamente consumate.
 */

export const AcubeEnvironmentSchema = z.enum(['sandbox', 'production']);
export type AcubeEnvironment = z.infer<typeof AcubeEnvironmentSchema>;

export const ACUBE_ENVIRONMENTS: ReadonlyArray<AcubeEnvironment> = AcubeEnvironmentSchema.options;

/** Ruolo dell'account con cui si effettua il login su common.api: supplier = account integratore, merchant = esercente e-receipts. */
export const AcubeAuthRoleSchema = z.enum(['supplier', 'merchant']);
export type AcubeAuthRole = z.infer<typeof AcubeAuthRoleSchema>;

/** Servizi A-Cube raggiunti dal client, ognuno con il proprio host per ambiente. */
export const AcubeServiceSchema = z.enum(['common', 'govit', 'ereceipts']);
export type AcubeService = z.infer<typeof AcubeServiceSchema>;

export const ACUBE_HOSTS: Readonly<Record<AcubeService, Readonly<Record<AcubeEnvironment, string>>>> = {
  common: {
    sandbox: 'https://common.api.acubeapi.com',
    production: 'https://common.api.acubeapi.com',
  },
  govit: {
    sandbox: 'https://api-sandbox.acubeapi.com',
    production: 'https://api.acubeapi.com',
  },
  ereceipts: {
    sandbox: 'https://ereceipts-it-sandbox.acubeapi.com',
    production: 'https://ereceipts-it.acubeapi.com',
  },
};

// ───────────────────────────── Login (common) ─────────────────────────────

export const AcubeLoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  environment: AcubeEnvironmentSchema,
});
export type AcubeLoginRequest = z.infer<typeof AcubeLoginRequestSchema>;

export const AcubeLoginResponseSchema = z.object({ token: z.string().min(1) });
export type AcubeLoginResponse = z.infer<typeof AcubeLoginResponseSchema>;

/** Esito della verifica credenziali esposto alla UI integrazione. */
export const AcubeCredentialsCheckSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
  environment: AcubeEnvironmentSchema,
  roles: z.array(z.string()),
  token_expires_at: z.string().nullable(),
});
export type AcubeCredentialsCheck = z.infer<typeof AcubeCredentialsCheckSchema>;

// ───────────────────────── Errori ufficiali remoti ────────────────────────

/** Problem Details (RFC 7807) restituito da E-Receipts IT. */
export const AcubeProblemDetailsSchema = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  status: z.number().int().optional(),
  detail: z.string().optional(),
  instance: z.string().nullable().optional(),
  violations: z.array(z.record(z.string())).optional(),
});
export type AcubeProblemDetails = z.infer<typeof AcubeProblemDetailsSchema>;

/** Errore di validazione FastAPI (422 e-receipts). */
export const AcubeHttpValidationErrorSchema = z.object({
  detail: z.array(z.object({
    loc: z.array(z.union([z.string(), z.number()])),
    msg: z.string(),
    type: z.string(),
  })),
});
export type AcubeHttpValidationError = z.infer<typeof AcubeHttpValidationErrorSchema>;

/** Errore piatto della piattaforma gov-it (`{code,message}`, con eventuali violations API Platform). */
export const AcubeGovItErrorSchema = z.object({
  code: z.union([z.string(), z.number()]).optional(),
  message: z.string().optional(),
  detail: z.string().optional(),
  'hydra:description': z.string().optional(),
  violations: z.array(z.object({ propertyPath: z.string().optional(), message: z.string().optional() })).optional(),
});
export type AcubeGovItError = z.infer<typeof AcubeGovItErrorSchema>;

/** Violazione normalizzata (indipendente dal servizio) esposta fino all'HTTP. */
export const AcubeViolationSchema = z.object({
  field: z.string().nullable(),
  message: z.string(),
});
export type AcubeViolation = z.infer<typeof AcubeViolationSchema>;

export const ACUBE_ERROR_CODES = [
  'acube_credentials_missing',
  'acube_unauthorized',
  'acube_not_onboarded',
  'acube_api_error',
  'acube_validation_error',
  'acube_invalid_fiscal_id',
  'acube_invalid_config',
  'acube_pem_missing',
  'acube_unsupported_operation',
  'acube_merchant_missing',
  'acube_cash_register_missing',
  'acube_vat_unmappable',
  'acube_payment_unmappable',
  'acube_webhook_registration_incomplete',
] as const;
export const AcubeErrorCodeSchema = z.enum(ACUBE_ERROR_CODES);
export type AcubeErrorCode = z.infer<typeof AcubeErrorCodeSchema>;

// ─────────────── Business Registry Configuration (gov-it) ────────────────

/** Schema ufficiale `ApiConfiguration-write` (gov-it): un webhook per evento. */
export const AcubeApiConfigurationWriteSchema = z.object({
  event: z.string().min(1),
  target_url: z.string().url(),
  authentication_type: z.enum(['query', 'header']).nullable(),
  authentication_key: z.string().min(1).nullable(),
  authentication_token: z.string().min(1).nullable(),
});
export type AcubeApiConfigurationWrite = z.infer<typeof AcubeApiConfigurationWriteSchema>;

/** Schema ufficiale `ApiConfiguration-read` (campi consumati dall'app). */
export const AcubeApiConfigurationReadSchema = z.object({
  uuid: z.string().min(1),
  event: z.string().min(1),
  target_url: z.string(),
  authentication_type: z.enum(['query', 'header']).nullable().optional(),
  authentication_key: z.string().nullable().optional(),
  authentication_token: z.string().nullable().optional(),
}).passthrough();
export type AcubeApiConfigurationRead = z.infer<typeof AcubeApiConfigurationReadSchema>;
export const AcubeApiConfigurationListSchema = z.array(AcubeApiConfigurationReadSchema);

/** Campi scrivibili dello schema ufficiale `BusinessRegistryConfiguration-write`. */
export const AcubeBusinessRegistryWriteSchema = z.object({
  fiscal_id: z.string().min(1),
  name: z.string().min(1).optional(),
  email: z.string().email().optional(),
  customer_invoice_enabled: z.boolean().optional(),
  supplier_invoice_enabled: z.boolean().optional(),
  receipts_enabled: z.boolean().optional(),
  apply_signature: z.boolean().optional(),
  apply_legal_storage: z.boolean().optional(),
  phone_number: z.string().optional(),
  api_configurations: z.array(AcubeApiConfigurationWriteSchema).optional(),
});
export type AcubeBusinessRegistryWrite = z.infer<typeof AcubeBusinessRegistryWriteSchema>;

/** Campi letti dello schema ufficiale `BusinessRegistryConfiguration-read` consumati dall'app. */
export const AcubeBusinessRegistryReadSchema = z.object({
  fiscal_id: z.string().min(1),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  customer_invoice_enabled: z.boolean().nullable().optional(),
  supplier_invoice_enabled: z.boolean().nullable().optional(),
  receipts_enabled: z.boolean().nullable().optional(),
  apply_signature: z.boolean().nullable().optional(),
  apply_legal_storage: z.boolean().nullable().optional(),
  legal_storage_active: z.boolean().nullable().optional(),
  phone_number: z.string().nullable().optional(),
  email_for_preservation_requested_at: z.string().nullable().optional(),
  api_configurations: z.array(AcubeApiConfigurationReadSchema).optional(),
}).passthrough();
export type AcubeBusinessRegistryRead = z.infer<typeof AcubeBusinessRegistryReadSchema>;

/** `GET /business-registry-configurations` restituisce una collezione piatta (API Platform JSON). */
export const AcubeBusinessRegistryListSchema = z.array(AcubeBusinessRegistryReadSchema);

// ───────────────────── Configurazione account (tenant) ────────────────────

export const AcubeSubjectTypeSchema = z.enum(['company', 'sole_proprietorship', 'freelancer']);
export type AcubeSubjectType = z.infer<typeof AcubeSubjectTypeSchema>;

export const AcubeServiceStatusValueSchema = z.enum(['configured', 'not_configured', 'error']);
export type AcubeServiceStatusValue = z.infer<typeof AcubeServiceStatusValueSchema>;

export const ACUBE_SERVICE_KEYS = ['einvoice', 'ereceipts', 'legal_storage', 'supplier_invoices'] as const;
export const AcubeServiceKeySchema = z.enum(ACUBE_SERVICE_KEYS);
export type AcubeServiceKey = z.infer<typeof AcubeServiceKeySchema>;

export const AcubeServiceStatusMapSchema = z.record(AcubeServiceKeySchema, AcubeServiceStatusValueSchema);
export type AcubeServiceStatusMap = z.infer<typeof AcubeServiceStatusMapSchema>;

/**
 * Eventi webhook gov-it registrati dall'app (una `ApiConfiguration` per evento
 * e per ragione sociale). Gli eventi Smart Receipt / SistemaTS non vengono
 * registrati: se arrivano sono archiviati come sconosciuti.
 */
export const ACUBE_WEBHOOK_EVENTS = [
  'customer-invoice',
  'customer-notification',
  'invoice-status-quarantena',
  'invoice-status-invoice-error',
  'supplier-invoice',
  'legal-storage-receipt',
  'legal-storage-missing-vat',
  'job',
  'job-error',
] as const;
export const AcubeWebhookEventSchema = z.enum(ACUBE_WEBHOOK_EVENTS);
export type AcubeWebhookEvent = z.infer<typeof AcubeWebhookEventSchema>;

/** Eventi registrati per servizio BRC attivo (`job`/`job-error` sempre). */
export const ACUBE_WEBHOOK_EVENTS_BY_SERVICE: Readonly<Record<AcubeServiceKey, ReadonlyArray<AcubeWebhookEvent>>> = {
  einvoice: ['customer-invoice', 'customer-notification', 'invoice-status-quarantena', 'invoice-status-invoice-error'],
  supplier_invoices: ['supplier-invoice'],
  legal_storage: ['legal-storage-receipt', 'legal-storage-missing-vat'],
  ereceipts: [],
};
export const ACUBE_WEBHOOK_EVENTS_ALWAYS: ReadonlyArray<AcubeWebhookEvent> = ['job', 'job-error'];

/** `GET https://common.api.acubeapi.com/signature-public-key?environment=` → PEM Ed25519. */
export const AcubeSignaturePublicKeyResponseSchema = z.object({ public_key: z.string().min(1) });
export type AcubeSignaturePublicKeyResponse = z.infer<typeof AcubeSignaturePublicKeyResponseSchema>;

/** Configurazione NON sensibile dell'account A-Cube (`integration_templates.config`). */
export const AcubeAccountConfigSchema = z.object({
  environment: AcubeEnvironmentSchema.optional(),
  subject_type: AcubeSubjectTypeSchema.optional(),
  denominazione: z.string().optional(),
  service_einvoice: z.boolean().optional(),
  service_ereceipts: z.boolean().optional(),
  service_legal_storage: z.boolean().optional(),
  service_supplier_invoices: z.boolean().optional(),
  service_legal_storage_ddt: z.boolean().optional(),
  service_legal_storage_contract: z.boolean().optional(),
  service_legal_storage_order: z.boolean().optional(),
  digital_signature_enabled: z.boolean().optional(),
  legal_storage_consent: z.boolean().optional(),
  legal_storage_consent_at: z.string().optional(),
  legal_representative_first_name: z.string().optional(),
  legal_representative_last_name: z.string().optional(),
  legal_representative_fiscal_code: z.string().optional(),
  legal_representative_role: z.string().optional(),
  service_status: AcubeServiceStatusMapSchema.optional(),
  last_onboard_error: z.string().optional(),
  last_onboard_at: z.string().optional(),
  tags: z.array(z.string()).optional(),
});
export type AcubeAccountConfig = z.infer<typeof AcubeAccountConfigSchema>;

/** Credenziali cifrate dell'account A-Cube (`integration_templates.credentials_encrypted`). */
export const AcubeAccountCredentialsSchema = z.object({
  email: z.string(),
  password: z.string(),
  /** Bearer inviato da A-Cube in `Authorization` su ogni webhook del tenant. */
  webhook_token: z.string().min(16).optional(),
  legal_representative_fiscal_code: z.string().optional(),
});
export type AcubeAccountCredentials = z.infer<typeof AcubeAccountCredentialsSchema>;

/**
 * Lettura del blob cifrato già salvato: i tenant configurati prima delle
 * `api_configurations` per evento hanno il segreto webhook sotto la chiave
 * `webhook_secret`. Lo stesso valore diventa il Bearer `webhook_token`, così la
 * ri-registrazione automatica e il ricevitore funzionano senza risalvataggio;
 * la prima scrittura successiva persiste il blob nella forma canonica.
 */
export const AcubeStoredAccountCredentialsSchema = AcubeAccountCredentialsSchema
  .extend({ webhook_secret: z.string().min(16).optional() })
  .transform(({ webhook_secret, ...creds }): AcubeAccountCredentials => ({
    ...creds,
    ...(creds.webhook_token === undefined && webhook_secret !== undefined ? { webhook_token: webhook_secret } : {}),
  }));

// ───────────────── Identità fiscale per ragione sociale ───────────────────

/** Subset A-Cube di `companies.fiscal_provider_config` (NON sensibile). */
export const AcubeCompanyConfigSchema = z.object({
  provider: z.literal('acube'),
  einvoice_provider: z.enum(['acube', 'fic']).optional(),
  fic_company_id: z.number().int().positive().optional(),
  environment: AcubeEnvironmentSchema.optional(),
  fiscal_id: z.string().min(1).optional(),
  customer_invoice_enabled: z.boolean().optional(),
  supplier_invoice_enabled: z.boolean().optional(),
  receipts_enabled: z.boolean().optional(),
  apply_signature: z.boolean().optional(),
  apply_legal_storage: z.boolean().optional(),
  legal_storage_active: z.boolean().optional(),
  brc_synced_at: z.string().optional(),
  webhook_events_registered: z.array(AcubeWebhookEventSchema).optional(),
  webhook_synced_at: z.string().optional(),
});
export type AcubeCompanyConfig = z.infer<typeof AcubeCompanyConfigSchema>;

/** Stato BRC per RS mostrato nella UI integrazione / scheda fiscale. */
export const AcubeCompanyBrcStatusSchema = z.object({
  company_id: z.string().uuid(),
  legal_name: z.string().nullable(),
  fiscal_id: z.string().nullable(),
  environment: AcubeEnvironmentSchema.nullable(),
  brc_synced_at: z.string().nullable(),
  customer_invoice_enabled: z.boolean(),
  supplier_invoice_enabled: z.boolean(),
  receipts_enabled: z.boolean(),
  apply_legal_storage: z.boolean(),
  legal_storage_active: z.boolean(),
  webhook_events_registered: z.array(AcubeWebhookEventSchema),
  webhook_synced_at: z.string().nullable(),
});
export type AcubeCompanyBrcStatus = z.infer<typeof AcubeCompanyBrcStatusSchema>;

/** Esito onboarding BRC per singola RS (sync-business-registry, integration-status). */
export const AcubeOnboardEntrySchema = z.object({
  company_id: z.string().uuid(),
  fiscal_id: z.string().nullable(),
  action: z.enum(['created', 'updated']).nullable(),
  configuration: AcubeBusinessRegistryReadSchema.nullable(),
  error: z.string().nullable(),
});
export type AcubeOnboardEntry = z.infer<typeof AcubeOnboardEntrySchema>;

/** Politica ambiente per il tenant (UI integrazione). */
export const AcubeEnvironmentPolicySchema = z.object({
  is_development: z.boolean(),
  sandbox_allowed: z.boolean(),
  tenant_slug: z.string(),
  default_environment: AcubeEnvironmentSchema,
  active_environment: AcubeEnvironmentSchema,
});
export type AcubeEnvironmentPolicy = z.infer<typeof AcubeEnvironmentPolicySchema>;

/** `GET /integrations/acube/state`: config account + politica ambiente + BRC per RS. */
/** Stato della chiave pubblica di firma A-Cube in cache (current + eventuale "next"). */
export const AcubeWebhookPublicKeyEntrySchema = z.object({
  fingerprint: z.string(),
  fetched_at: z.string(),
  expires_at: z.string(),
});
export type AcubeWebhookPublicKeyEntry = z.infer<typeof AcubeWebhookPublicKeyEntrySchema>;

export const AcubeWebhookPublicKeyStateSchema = z.object({
  environment: AcubeEnvironmentSchema,
  current: AcubeWebhookPublicKeyEntrySchema.nullable(),
  next: AcubeWebhookPublicKeyEntrySchema.nullable(),
  error: z.string().nullable(),
});
export type AcubeWebhookPublicKeyState = z.infer<typeof AcubeWebhookPublicKeyStateSchema>;

/** Diagnostica webhook mostrata nel modale integrazione. */
export const AcubeWebhookDiagnosticsSchema = z.object({
  base_url: z.string(),
  has_token: z.boolean(),
  public_key: AcubeWebhookPublicKeyStateSchema,
});
export type AcubeWebhookDiagnostics = z.infer<typeof AcubeWebhookDiagnosticsSchema>;

export const AcubeIntegrationStateSchema = z.object({
  config: AcubeAccountConfigSchema,
  has_credentials: z.boolean(),
  environment_policy: AcubeEnvironmentPolicySchema,
  companies: z.array(AcubeCompanyBrcStatusSchema),
  webhook: AcubeWebhookDiagnosticsSchema,
});
export type AcubeIntegrationState = z.infer<typeof AcubeIntegrationStateSchema>;

export const AcubeServiceUiStatusSchema = z.object({
  enabled: z.boolean(),
  status: z.enum(['inactive', 'active', 'error', 'pending_remote']),
  remote: AcubeServiceStatusValueSchema,
  message: z.string().optional(),
});
export type AcubeServiceUiStatus = z.infer<typeof AcubeServiceUiStatusSchema>;

/** `POST /integrations/acube/sync-business-registry`. */
export const AcubeBusinessRegistrySyncResultSchema = z.object({
  environment: AcubeEnvironmentSchema,
  services: z.record(AcubeServiceKeySchema, AcubeServiceUiStatusSchema),
  entries: z.array(AcubeOnboardEntrySchema),
  companies: z.array(AcubeCompanyBrcStatusSchema),
});
export type AcubeBusinessRegistrySyncResult = z.infer<typeof AcubeBusinessRegistrySyncResultSchema>;

// ──────────── Identità e-receipts (merchant / PEM / cassa digitale) ───────

export const AcubePemStatusSchema = z.enum(['new', 'registered', 'activated', 'online', 'offline', 'discarded', 'error']);
export type AcubePemStatus = z.infer<typeof AcubePemStatusSchema>;

/** Stati del PEM che consentono operazioni fiscali (emissione, annullo, reso, chiusura). */
export const ACUBE_PEM_OPERATIVE_STATUSES: ReadonlyArray<AcubePemStatus> = ['activated', 'online', 'offline'];

export const AcubePemJournalStatusSchema = z.enum(['inactive', 'open', 'closed', 'sent', 'ready']);
export type AcubePemJournalStatus = z.infer<typeof AcubePemJournalStatusSchema>;

export const AcubeAddressSchema = z.object({
  street_address: z.string(),
  street_number: z.string(),
  zip_code: z.string(),
  city: z.string(),
  province: z.string(),
});
export type AcubeAddress = z.infer<typeof AcubeAddressSchema>;

/** Riga locale `acube_merchants` esposta alla UI (mai la password). */
export const AcubeMerchantSchema = z.object({
  id: z.string().uuid(),
  company_id: z.string().uuid(),
  merchant_uuid: z.string().min(1),
  merchant_email: z.string().email(),
  environment: AcubeEnvironmentSchema,
  created_at: z.string(),
  updated_at: z.string(),
});
export type AcubeMerchant = z.infer<typeof AcubeMerchantSchema>;

export const AcubePemSchema = z.object({
  id: z.string().uuid(),
  store_id: z.string().uuid(),
  merchant_id: z.string().uuid(),
  serial_number: z.string().min(1),
  status: AcubePemStatusSchema,
  journal_status: AcubePemJournalStatusSchema.nullable(),
  address: AcubeAddressSchema.nullable(),
  last_error: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AcubePem = z.infer<typeof AcubePemSchema>;

/** Riga locale `acube_cash_registers` esposta alla UI (certificato e chiave mTLS mai esposti). */
export const AcubeCashRegisterSchema = z.object({
  id: z.string().uuid(),
  printer_id: z.string().uuid(),
  pem_id: z.string().uuid(),
  cash_register_uuid: z.string().min(1),
  issued_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AcubeCashRegister = z.infer<typeof AcubeCashRegisterSchema>;

/** Stato E-Receipts di una ragione sociale (scheda RS / modale registratore). */
export const AcubeCompanyEreceiptsStatusSchema = z.object({
  company_id: z.string().uuid(),
  merchant: AcubeMerchantSchema.nullable(),
  pems: z.array(AcubePemSchema),
});
export type AcubeCompanyEreceiptsStatus = z.infer<typeof AcubeCompanyEreceiptsStatusSchema>;

/** Stato E-Receipts di una postazione digitale (PEM della sede + cassa). */
export const AcubePrinterEreceiptsStatusSchema = z.object({
  printer_id: z.string().uuid(),
  store_id: z.string().uuid().nullable(),
  company_id: z.string().uuid().nullable(),
  merchant: AcubeMerchantSchema.nullable(),
  pem: AcubePemSchema.nullable(),
  cash_register: AcubeCashRegisterSchema.nullable(),
});
export type AcubePrinterEreceiptsStatus = z.infer<typeof AcubePrinterEreceiptsStatusSchema>;

// ───────────────────────── E-Receipts: mf2 provisioning ───────────────────

export const AcubeMerchantCreateRequestSchema = z.object({
  vat_number: z.string().min(1),
  fiscal_code: z.string().min(1),
  business_name: z.string().min(1),
  email: z.string().email(),
  password: z.string().min(8),
  address: AcubeAddressSchema,
});
export type AcubeMerchantCreateRequest = z.infer<typeof AcubeMerchantCreateRequestSchema>;

export const AcubeMerchantOutputSchema = z.object({
  uuid: z.string().min(1),
  vat_number: z.string().optional(),
  fiscal_code: z.string().nullable().optional(),
  business_name: z.string().optional(),
  email: z.string().optional(),
  address: AcubeAddressSchema.nullable().optional(),
}).passthrough();
export type AcubeMerchantOutput = z.infer<typeof AcubeMerchantOutputSchema>;

export const AcubeMerchantPageSchema = z.object({
  members: z.array(AcubeMerchantOutputSchema),
  total: z.number().int(),
  page: z.number().int(),
  size: z.number().int(),
  pages: z.number().int(),
});
export type AcubeMerchantPage = z.infer<typeof AcubeMerchantPageSchema>;

export const AcubePemCreateRequestSchema = z.object({
  merchant_uuid: z.string().min(1),
  address: AcubeAddressSchema,
});
export type AcubePemCreateRequest = z.infer<typeof AcubePemCreateRequestSchema>;

export const AcubePemCreateOutputSchema = z.object({
  serial_number: z.string().min(1),
  registration_key: z.string().min(1),
}).passthrough();
export type AcubePemCreateOutput = z.infer<typeof AcubePemCreateOutputSchema>;

export const AcubePemOutputSchema = z.object({
  serial_number: z.string(),
  status: AcubePemStatusSchema,
  address: AcubeAddressSchema.nullable().optional(),
  journal_status: AcubePemJournalStatusSchema.nullable().optional(),
}).passthrough();
export type AcubePemOutput = z.infer<typeof AcubePemOutputSchema>;

export const AcubePemPageSchema = z.object({
  members: z.array(AcubePemOutputSchema),
  total: z.number().int(),
  page: z.number().int(),
  size: z.number().int(),
  pages: z.number().int(),
});
export type AcubePemPage = z.infer<typeof AcubePemPageSchema>;

export const AcubePemActivationRequestSchema = z.object({ registration_key: z.string().min(1) });
export type AcubePemActivationRequest = z.infer<typeof AcubePemActivationRequestSchema>;

export const AcubeReceiptFormatSchema = z.enum(['58mm', '80mm']);
export type AcubeReceiptFormat = z.infer<typeof AcubeReceiptFormatSchema>;

/** `PUT /v1/mf2/pems/{sn}/configuration`: intestazione/piè di pagina/logo dello scontrino. */
export const AcubePemConfigurationSchema = z.object({
  receipt_header: z.array(z.string()).optional(),
  footer_text: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  receipt_format: AcubeReceiptFormatSchema.optional(),
});
export type AcubePemConfiguration = z.infer<typeof AcubePemConfigurationSchema>;

export const AcubeCashRegisterCreateRequestSchema = z.object({
  pem_serial_number: z.string().min(1),
  name: z.string().min(1),
});
export type AcubeCashRegisterCreateRequest = z.infer<typeof AcubeCashRegisterCreateRequestSchema>;

/** Risposta di creazione cassa: certificato e chiave privata mTLS (PEM con newline escaped). */
export const AcubeCashRegisterCreateOutputSchema = z.object({
  uuid: z.string().min(1),
  name: z.string().optional(),
  mtls_certificate: z.string().min(1),
  private_key: z.string().min(1),
}).passthrough();
export type AcubeCashRegisterCreateOutput = z.infer<typeof AcubeCashRegisterCreateOutputSchema>;

export const AcubeCashRegisterOutputSchema = z.object({
  uuid: z.string().min(1),
  name: z.string().optional(),
  pem_serial_number: z.string().optional(),
}).passthrough();
export type AcubeCashRegisterOutput = z.infer<typeof AcubeCashRegisterOutputSchema>;

// ───────────────────────── E-Receipts: mf1 operatività ────────────────────

/** Catalogo chiuso ufficiale `VatRateEnum` (aliquote ordinarie + agricole). */
export const ACUBE_VAT_RATE_CODES = ['2.00', '4.00', '5.00', '6.40', '7.00', '7.30', '7.50', '7.65', '7.95', '8.30', '8.50', '8.80', '9.50', '10.00', '12.30', '22.00'] as const;
export const AcubeVatRateCodeSchema = z.enum(ACUBE_VAT_RATE_CODES);
export type AcubeVatRateCode = z.infer<typeof AcubeVatRateCodeSchema>;

/** Nature IVA accettate da E-Receipts per righe esenti/non imponibili (radici, niente sottocodici né N7). */
export const ACUBE_NATURE_CODES = ['N1', 'N2', 'N3', 'N4', 'N5', 'N6'] as const;
export const AcubeNatureCodeSchema = z.enum(ACUBE_NATURE_CODES);
export type AcubeNatureCode = z.infer<typeof AcubeNatureCodeSchema>;

export const AcubeReceiptItemTypeSchema = z.enum(['goods', 'service']);
export type AcubeReceiptItemType = z.infer<typeof AcubeReceiptItemTypeSchema>;

export const AcubeReceiptItemSchema = z.object({
  type: AcubeReceiptItemTypeSchema,
  quantity: z.string().min(1),
  description: z.string().min(1).max(1000),
  unit_price: z.string().min(1),
  vat_rate_code: z.union([AcubeVatRateCodeSchema, AcubeNatureCodeSchema]).nullable(),
  simplified_vat_allocation: z.boolean().optional(),
  discount: z.string().optional(),
  complimentary: z.boolean().optional(),
});
export type AcubeReceiptItem = z.infer<typeof AcubeReceiptItemSchema>;

export const AcubePaymentTypeSchema = z.enum(['cash', 'electronic', 'ticket', 'discount', 'credit', 'uncollected_services', 'rounding_up', 'rounding_down']);
export type AcubePaymentType = z.infer<typeof AcubePaymentTypeSchema>;

export const AcubePaymentDetailSchema = z.object({
  type: AcubePaymentTypeSchema,
  amount: z.string().min(1),
  ticket_quantity: z.number().int().positive().optional(),
  prepaid_or_voucher: z.boolean().optional(),
});
export type AcubePaymentDetail = z.infer<typeof AcubePaymentDetailSchema>;

/** Body `POST /v1/mf1/receipts`. `customer_tax_code` e `customer_lottery_code` sono mutuamente esclusivi. */
export const AcubeReceiptCreateRequestSchema = z.object({
  items: z.array(AcubeReceiptItemSchema).min(1),
  payment_details: z.array(AcubePaymentDetailSchema),
  customer_lottery_code: z.string().optional(),
  customer_tax_code: z.string().optional(),
  invoice_issuing: z.boolean().optional(),
  uncollected_dcr_to_ssn: z.boolean().optional(),
  services_uncollected_amount: z.string().optional(),
  goods_uncollected_amount: z.string().optional(),
  discount: z.string().optional(),
}).refine((v) => !(v.customer_lottery_code && v.customer_tax_code), { message: 'customer_lottery_code e customer_tax_code sono alternativi' });
export type AcubeReceiptCreateRequest = z.infer<typeof AcubeReceiptCreateRequestSchema>;

export const AcubeReceiptTypeSchema = z.enum(['sale', 'return', 'void']);
export const AcubeReceiptStatusSchema = z.enum(['ready', 'sent', 'error']);
export type AcubeReceiptStatus = z.infer<typeof AcubeReceiptStatusSchema>;

export const AcubeReceiptOutputSchema = z.object({
  uuid: z.string(),
  type: z.string(),
  total_amount: z.string().nullable().optional(),
  document_number: z.string(),
  document_datetime: z.string(),
  parent_receipt_uuid: z.string().nullable().optional(),
  is_voidable: z.boolean().optional(),
  is_returnable: z.boolean().optional(),
  pdf_url: z.string().nullable().optional(),
  status: z.string(),
}).passthrough();
export type AcubeReceiptOutput = z.infer<typeof AcubeReceiptOutputSchema>;

export const AcubeReceiptVoidRequestSchema = z.object({ document_number: z.string().min(1) });
export type AcubeReceiptVoidRequest = z.infer<typeof AcubeReceiptVoidRequestSchema>;

export const AcubeReturnItemSchema = z.object({ id: z.string().min(1), quantity: z.string().min(1) });
export type AcubeReturnItem = z.infer<typeof AcubeReturnItemSchema>;

export const AcubeReceiptReturnRequestSchema = z.object({
  document_number: z.string().min(1),
  items: z.array(AcubeReturnItemSchema).min(1),
});
export type AcubeReceiptReturnRequest = z.infer<typeof AcubeReceiptReturnRequestSchema>;

/** `GET /v1/mf1/receipts/{uuid}/returnable-items`: righe ancora restituibili con quantità residua. */
export const AcubeReturnableItemSchema = z.object({
  id: z.string().min(1),
  description: z.string(),
  quantity: z.string(),
  unit_price: z.string().optional(),
  vat_rate_code: z.string().nullable().optional(),
}).passthrough();
export type AcubeReturnableItem = z.infer<typeof AcubeReturnableItemSchema>;
export const AcubeReturnableItemsSchema = z.array(AcubeReturnableItemSchema);

/** Esito `POST /v1/mf1/pems/{sn}/close`. */
export const AcubePemCloseOutputSchema = z.object({
  serial_number: z.string().optional(),
  journal_status: AcubePemJournalStatusSchema.nullable().optional(),
  closed_at: z.string().nullable().optional(),
}).passthrough();
export type AcubePemCloseOutput = z.infer<typeof AcubePemCloseOutputSchema>;

/** `GET /v1/mf1/pems/telemetry`: stato live del PEM per cassa. */
export const AcubePemTelemetrySchema = z.object({
  serial_number: z.string(),
  status: AcubePemStatusSchema,
  journal_status: AcubePemJournalStatusSchema.nullable().optional(),
  pending_receipts: z.number().int().nonnegative().optional(),
  last_transmission_at: z.string().nullable().optional(),
  last_receipt_at: z.string().nullable().optional(),
}).passthrough();
export type AcubePemTelemetry = z.infer<typeof AcubePemTelemetrySchema>;
export const AcubePemTelemetryListSchema = z.array(AcubePemTelemetrySchema);

/** `GET /v1/mf1/notifications`: notifiche A-Cube per il merchant (errori AdE, trasmissioni). */
export const AcubeNotificationSchema = z.object({
  uuid: z.string(),
  type: z.string(),
  message: z.string().nullable().optional(),
  created_at: z.string(),
  pem_serial_number: z.string().nullable().optional(),
  read: z.boolean().optional(),
}).passthrough();
export type AcubeNotification = z.infer<typeof AcubeNotificationSchema>;
export const AcubeNotificationPageSchema = z.object({
  members: z.array(AcubeNotificationSchema),
  total: z.number().int(),
  page: z.number().int(),
  size: z.number().int(),
  pages: z.number().int(),
});
export type AcubeNotificationPage = z.infer<typeof AcubeNotificationPageSchema>;

// ────────────────────────────── Fatture gov-it ────────────────────────────
